import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Survey } from '../models/survey/survey';
import { SendTeamSurvey } from 'src/app/shared/models/team/sendTeamSurvey';
import { SurveyAggregate } from 'src/app/shared/models/survey/surveyAggregate';
import { SurveyForm } from '../models/survey/surveyForm';
import { AttributeWrite } from '../models/attributes/attributeWrite';
import { BaseService } from './base.service';
import { SurveyStatus } from '../models/survey/surveyStatus';
import { MembersService } from 'src/app/shared/services/members.service';
import { download } from 'src/app/shared/utils/download-file';
import { SurveyResponse } from '../models/survey/surveyResponse';
import { SurveyMode } from 'src/shared-app-portal/components/lineup-applications/survey/surveyMode';

@Injectable({
  providedIn: 'root'
})
export class SurveyService extends BaseService {
  public readonly isLoadingOrSaving = new BehaviorSubject<boolean>(true);
  private _surveyResponse = new BehaviorSubject<SurveyResponse>(null);
  private _surveyAggregate = new BehaviorSubject<SurveyAggregate>(null);
  public readonly surveyResponse: Observable<SurveyResponse> = this._surveyResponse.asObservable();
  public readonly surveyAggregate: Observable<SurveyAggregate> =
    this._surveyAggregate.asObservable();

  constructor(private membersService: MembersService) {
    super();
  }

  private _secureLinkId: string;
  get secureLinkId(): string {
    return this._secureLinkId;
  }
  public readonly survey = new BehaviorSubject<SurveyForm>(null);
  public readonly surveyPreview = new BehaviorSubject<SurveyForm>(null);
  public get navigationPath(): string {
    return `/members/survey/${this.secureLinkId}`;
  }
  private _surveyStarted = false;

  setIsLoadingOrSaving(isLoadingOrSaving: boolean) {
    this.isLoadingOrSaving.next(isLoadingOrSaving);
  }

  isEnabled(): boolean {
    return true;
  }

  startSurvey() {
    this._surveyStarted = true;
    this.router.navigate([`${this.navigationPath}/${SurveyMode.Respond}`]);
  }

  getSurveyFormBySecureLinkId(secureLinkId: string) {
    this._secureLinkId = secureLinkId;
    this.get(this.getMembersBasePath(secureLinkId)).subscribe((data) => {
      if (data) {
        this.survey.next(SurveyForm.parse(data));
        this.membersService.workspaceLogoUrl.next(data.form.workspaceLogoUrl);
        this.membersService.workspaceHelpLink.next(data.form.workspaceHelpLink);
      }
    });
  }
  public setSurveyForm() {
    switch (this.survey.value.status) {
      case SurveyStatus.Expired:
        this.router.navigate([`${this.navigationPath}/${SurveyMode.Expired}`]);
        break;
      case SurveyStatus.Complete:
        this.router.navigate([`${this.navigationPath}/${SurveyMode.Confirmation}`]);
        break;
      default:
        if (!this._surveyStarted && this.survey.value.hasIntroduction) {
          this.router.navigate([`${this.navigationPath}/${SurveyMode.Introduction}`]);
        } else if (!this._surveyStarted && !this.survey.value.hasIntroduction) {
          this.router.navigate([`${this.navigationPath}/${SurveyMode.Respond}`]);
        }
        break;
    }
  }
  updateSurvey(attributes: any) {
    let body = JSON.stringify(attributes);
    this.post(this.getMembersBasePath(this._secureLinkId), body).subscribe((data) => {
      if (data) {
        this.survey.next(SurveyForm.parse(data));
      }
    });
  }
  updateSurveyForm(teamId: string, formId: string, deadline?: Date): Observable<any> {
    let body = JSON.stringify({ formId: formId, deadline: deadline });
    return this.put(this.getBasePath(teamId), body);
  }

  sendForTeam(teamId: string, send: SendTeamSurvey): Observable<Survey[]> {
    let body = JSON.stringify(send);
    let obs = this.post(this.getBasePath(teamId), body).pipe(
      map((data) => data.map((survey) => Survey.parse(survey)))
    );
    return obs;
  }

  sendMore(teamId: string, surveyId: string, send: SendTeamSurvey): Observable<Survey[]> {
    let body = JSON.stringify(send);
    let obs = this.put(`${this.getBasePath(teamId)}/${surveyId}`, body).pipe(
      map((data) => data.map((survey) => Survey.parse(survey)))
    );
    return obs;
  }

  sendSurveyReminder(teamId: string, surveyId: string, send: SendTeamSurvey): Observable<Survey[]> {
    let body = JSON.stringify(send);
    return this.post(`${this.getBasePath(teamId)}/${surveyId}`, body).pipe(
      map((data) => data.map((survey) => Survey.parse(survey)))
    );
  }

  getAggregateForSurvey(teamId: string, surveyId: string): Observable<SurveyAggregate> {
    let obs = this.get(`${this.getBasePath(teamId)}/${surveyId}/aggregates`).pipe(
      map((data) => SurveyAggregate.parse(data))
    );
    obs.subscribe((data) => {
      if (data) {
        this._surveyAggregate.next(data);
      }
    });

    return obs;
  }

  getRecipientsForSurvey(teamId: string, surveyId: string): Observable<SurveyResponse> {
    let obs = this.get(`${this.getBasePath(teamId)}/${surveyId}/recipients`);
    obs.subscribe((data) => {
      if (data) {
        this._surveyResponse.next(data);
      }
    });
    return obs;
  }

  deleteTeamSurvey(teamId: string, surveyId: string): Observable<Survey[]> {
    return this.delete(`${this.getBasePath(teamId)}/${surveyId}`).pipe(
      map((data) => data.map((survey) => Survey.parse(survey)))
    );
  }

  getAllForTeam(teamId: string): Observable<Survey[]> {
    return this.get(this.getBasePath(teamId)).pipe(
      map((data) => data.map((survey) => Survey.parse(survey)))
    );
  }

  getSurveyInTeam(teamId: string, surveyId: string): Observable<SurveyResponse> {
    return this.get(`${this.getBasePath(teamId)}/${surveyId}`);
  }

  downloadFile(requestUrl: string, fileName: string) {
    const encodedFileName = encodeURIComponent(fileName);
    const path = this.getMembersBasePath(requestUrl).concat(`?fileName=${encodedFileName}`);
    this.getFile(path).subscribe((res) => {
      download(res, fileName);
    });
  }

  private getBasePath(teamId: string): string {
    return `teams/${teamId}/surveys`;
  }

  private getMembersBasePath(secureLinkId: string): string {
    return `members/surveys/${secureLinkId}`;
  }

  getForPreview(formId: string) {
    let path = `survey-definitions/${formId}/preview`;
    this.get(path).subscribe((data) => {
      if (data) {
        this.surveyPreview.next(SurveyForm.parse(data));
        this.membersService.workspaceLogoUrl.next(data.form.workspaceLogoUrl);
      }
    });
  }
}
